// @ts-nocheck
// ============================================================
// STORE INTEGRITY VALIDATOR — Fase 12.6
// Assertions runtime sobre shape dos stores Zustand
// Detecta: state corruption, duplicatas, refs inválidas, stale
// ============================================================
import { obs } from '@/core/observability'
import { useTwinStore } from '@/store/twinStore'
import { useVisionStore } from '@/store/visionStore'
import { useTVStore } from '@/store/tvStore'

export type IntegrityLevel = 'ok' | 'warning' | 'error'

export interface IntegrityViolation {
  store:    string
  field:    string
  message:  string
  level:    IntegrityLevel
  value?:   unknown
}

export interface IntegrityReport {
  timestamp:  string
  violations: IntegrityViolation[]
  ok:         boolean
  stores:     Record<string, IntegrityLevel>
}

// ─── Assertion helpers ────────────────────────────────────
function assert(
  cond:    boolean,
  store:   string,
  field:   string,
  message: string,
  level:   IntegrityLevel,
  acc:     IntegrityViolation[],
  value?:  unknown,
): void {
  if (!cond) acc.push({ store, field, message, level, value })
}

function assertUnique<T>(
  items:   T[],
  key:     keyof T,
  store:   string,
  field:   string,
  acc:     IntegrityViolation[],
): void {
  const seen = new Set<unknown>()
  for (const item of items) {
    const v = item[key]
    if (seen.has(v)) {
      acc.push({ store, field, message: `Duplicate ${String(key)}: ${String(v)}`, level: 'error', value: v })
      return
    }
    seen.add(v)
  }
}

function assertBounded<T>(
  items:   T[],
  max:     number,
  store:   string,
  field:   string,
  acc:     IntegrityViolation[],
): void {
  assert(
    items.length <= max,
    store, field,
    `Collection exceeds bound: ${items.length} > ${max}`,
    'warning',
    acc,
    items.length,
  )
}

// ─── Per-store validators ─────────────────────────────────

function validateTwinStore(acc: IntegrityViolation[]): void {
  const s = 'twinStore'
  try {
    const state = useTwinStore.getState()

    assert(Array.isArray(state.correlations),   s, 'correlations',      'Not an array',       'error', acc)
    assert(Array.isArray(state.autonomousActions), s, 'autonomousActions', 'Not an array',     'error', acc)
    assert(Array.isArray(state.recommendations),  s, 'recommendations',   'Not an array',     'error', acc)

    assertBounded(state.correlations,   100, s, 'correlations',      acc)
    assertBounded(state.autonomousActions, 50, s, 'autonomousActions', acc)
    assertBounded(state.recommendations,  20, s, 'recommendations',   acc)

    assertUnique(state.correlations,     'id', s, 'correlations',     acc)
    assertUnique(state.autonomousActions,'id', s, 'autonomousActions', acc)

    if (state.twinState) {
      assert(Array.isArray(state.twinState.zones),    s, 'zones',     'Not an array', 'error', acc)
      assert(Array.isArray(state.twinState.risks),    s, 'risks',     'Not an array', 'error', acc)
      assert(Array.isArray(state.twinState.equipment),s, 'equipment', 'Not an array', 'error', acc)
      assertUnique(state.twinState.zones,     'id', s, 'zones',     acc)
      assertUnique(state.twinState.equipment, 'id', s, 'equipment', acc)

      assert(
        typeof state.overallRiskScore === 'number' && state.overallRiskScore >= 0 && state.overallRiskScore <= 100,
        s, 'overallRiskScore', `Invalid score: ${state.overallRiskScore}`, 'warning', acc,
      )
    }
  } catch (e) {
    acc.push({ store: s, field: 'access', message: `Store access error: ${String(e)}`, level: 'error' })
  }
}

function validateVisionStore(acc: IntegrityViolation[]): void {
  const s = 'visionStore'
  try {
    const state = useVisionStore.getState()

    assert(Array.isArray(state.events),      s, 'events',     'Not an array', 'error', acc)
    assert(Array.isArray(state.cameras),     s, 'cameras',    'Not an array', 'error', acc)
    assert(Array.isArray(state.violations),  s, 'violations', 'Not an array', 'error', acc)  // FIX: was 'alerts' — field is 'violations'

    assertBounded(state.events,     500, s, 'events',     acc)
    assertBounded(state.violations, 200, s, 'violations', acc)  // FIX: was 'alerts'

    assertUnique(state.cameras, 'id', s, 'cameras', acc)
    assertUnique(state.events,  'id', s, 'events',  acc)

  } catch (e) {
    acc.push({ store: s, field: 'access', message: `Store access error: ${String(e)}`, level: 'error' })
  }
}

function validateTVStore(acc: IntegrityViolation[]): void {
  const s = 'tvStore'
  try {
    const state = useTVStore.getState()

    assert(typeof state.profile === 'object' && state.profile !== null, s, 'profile', 'Invalid profile', 'error', acc)
    assert(Array.isArray(state.profile?.slides), s, 'slides', 'Not an array', 'error', acc)
    assert(
      state.currentSlideIndex >= 0,
      s, 'currentSlideIndex', `Negative index: ${state.currentSlideIndex}`, 'error', acc,
    )

    if (Array.isArray(state.profile?.slides)) {
      const enabled = state.profile.slides.filter(sl => sl.enabled)
      if (enabled.length > 0) {
        assert(
          state.currentSlideIndex < enabled.length,
          s, 'currentSlideIndex',
          `Index ${state.currentSlideIndex} out of range (max ${enabled.length - 1})`,
          'warning', acc,
        )
      }
    }

    assertBounded(state.tickerItems, 30, s, 'tickerItems', acc)

  } catch (e) {
    acc.push({ store: s, field: 'access', message: `Store access error: ${String(e)}`, level: 'error' })
  }
}

// ─── Public API ───────────────────────────────────────────
export function runIntegrityCheck(): IntegrityReport {
  const violations: IntegrityViolation[] = []

  validateTwinStore(violations)
  validateVisionStore(violations)
  validateTVStore(violations)

  // Per-store summary
  const stores: Record<string, IntegrityLevel> = {}
  for (const v of violations) {
    const prev = stores[v.store] ?? 'ok'
    if (v.level === 'error' || (v.level === 'warning' && prev === 'ok')) {
      stores[v.store] = v.level
    }
  }

  const ok = violations.every(v => v.level !== 'error')

  if (!ok) {
    obs.error('integrity', `Store integrity violations: ${violations.length}`, {
      errors:   violations.filter(v => v.level === 'error').length,
      warnings: violations.filter(v => v.level === 'warning').length,
    })
  }

  return { timestamp: new Date().toISOString(), violations, ok, stores }
}

// ─── Continuous integrity monitor ────────────────────────
let _integrityTimer: ReturnType<typeof setInterval> | null = null
const _integrityListeners: Array<(r: IntegrityReport) => void> = []

export function startIntegrityMonitor(intervalMs = 30_000): () => void {
  if (_integrityTimer) clearInterval(_integrityTimer)
  _integrityTimer = setInterval(() => {
    const report = runIntegrityCheck()
    _integrityListeners.forEach(fn => fn(report))
  }, intervalMs)

  return () => {
    if (_integrityTimer) { clearInterval(_integrityTimer); _integrityTimer = null }
  }
}

export function onIntegrityReport(fn: (r: IntegrityReport) => void): () => void {
  _integrityListeners.push(fn)
  return () => {
    const i = _integrityListeners.indexOf(fn)
    if (i !== -1) _integrityListeners.splice(i, 1)
  }
}
